import { put, takeEvery, call, takeLatest } from 'redux-saga/effects'
import { AsyncAction } from 'src/utils/reduxUtils'
import {
  FetchContractors,
  ExportContractorsCSV,
  FetchContractor,
  FetchContractorsCounts,
  SendRemainderContractor,
  FetchContractorActivitiesLogs,
  FetchSubcontractorCounts,
  FetchSubcontractor,
  UpdateSubcontractorMarket,
  UpdateSubcontractorExternalId,
  FetchContractorSelfie,
  FetchSensitiveData,
  FetchContractorDeactivationDates,
  SetContractorDeactivationDate,
  RemoveContractorDeactivationDate,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import { ApiService, callSecureApi } from './api/callApi'
import {
  Contractor,
  ContractorActivitiesLog,
  ContractorDetails,
  ContractorsCounts,
} from 'src/interfaces/contractor'
import {
  ContractorState,
  ContractorStatus,
  ExpirationIndicator,
} from 'src/constants/contractor/enums'
import { Pagination } from 'src/interfaces/table'
import { captureSentryError } from 'src/utils/sentry'
import type { SendReminderResponse } from 'src/interfaces/reminder'

export interface FetchContractorsPayload {
  contractorState: ContractorState
  partnerMarketId?: string
  contractorStatus?: ContractorStatus
  tags?: string[]
  collectDataTemplateIds?: string[]
  recollection?: string[]
  expirationIndicator?: ExpirationIndicator[]
  contractorSearch?: string
  limit: number
  offset: number
  paymentsAvailableOnly?: boolean
  sort?: string
  masterContractorRelationId?: string | null
  onlySubcontractors?: boolean
  autoActivationEnabled?: boolean
  vehicleType?: Array<string>
  onboardedAfter?: string
  onboardedBefore?: string
}

function* onFetchContractors(
  action: AsyncAction<FetchContractorsPayload, { data: Array<Contractor>; pagination: Pagination }>,
) {
  const {
    contractorState,
    partnerMarketId,
    contractorStatus,
    tags,
    collectDataTemplateIds,
    recollection,
    expirationIndicator,
    contractorSearch,
    paymentsAvailableOnly,
    limit,
    offset,
    sort,
    masterContractorRelationId,
    onlySubcontractors,
    autoActivationEnabled,
    vehicleType,
    onboardedAfter,
    onboardedBefore,
  } = action.payload

  try {
    const query: any = {
      contractorState,
      partnerMarketId,
      contractorStatus,
      tags,
      collectDataTemplateIds,
      recollection,
      expirationIndicator,
      contractorSearch,
      limit,
      offset,
      sort,
      masterContractorRelationId,
      onlySubcontractors,
      vehicleType,
      onboardedAfter,
      onboardedBefore,
    }

    if (paymentsAvailableOnly) {
      query.paymentsAvailableOnly = `${paymentsAvailableOnly}`
    }

    // Boolean filter — `false` is meaningful, so only send when explicitly set.
    if (autoActivationEnabled !== undefined) {
      query.autoActivationEnabled = `${autoActivationEnabled}`
    }

    const { payload }: { payload: { data: Array<Contractor>; pagination: Pagination } } =
      yield call(callSecureApi, Endpoints.FetchContractors, {
        apiService: ApiService.Paraworks,
        includeQueryNulls: true,
        query,
      })

    yield put(FetchContractors.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch contractors'

    yield put(FetchContractors.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export interface ExportContractorsCSVPayload {
  contractorState: ContractorState
  partnerMarketId?: string
  contractorStatus?: ContractorStatus
  tags?: string[]
  collectDataTemplateIds?: string[]
  recollection?: string[]
  expirationIndicator?: ExpirationIndicator[]
  contractorSearch?: string
  limit: number
  masterContractorRelationId?: string | null
  onlySubcontractors?: boolean
  sort?: string
  exportColumns: { header: string; key: string }[]
  onboardedAfter?: string
  onboardedBefore?: string
  vehicleType?: Array<string>
}

function* onExportContractorsCSV(action: AsyncAction<ExportContractorsCSVPayload, ArrayBuffer>) {
  const {
    contractorState,
    partnerMarketId,
    contractorStatus,
    tags,
    collectDataTemplateIds,
    recollection,
    expirationIndicator,
    contractorSearch,
    limit,
    masterContractorRelationId,
    onlySubcontractors,
    sort,
    exportColumns,
    onboardedAfter,
    onboardedBefore,
    vehicleType,
  } = action.payload

  try {
    const body: any = {
      contractorState,
      partnerMarketId,
      contractorStatus,
      tags,
      collectDataTemplateIds,
      recollection,
      expirationIndicator,
      contractorSearch,
      limit,
      masterContractorRelationId,
      onlySubcontractors,
      sort,
      exportColumns,
      onboardedAfter,
      onboardedBefore,
      vehicleType,
    }

    const { payload }: { payload: ArrayBuffer } = yield call(
      callSecureApi,
      Endpoints.ExportContractorsCSV,
      {
        apiService: ApiService.Paraworks,
        method: 'POST',
        includeQueryNulls: true,
        body,
        returnBuffer: true,
      },
    )

    yield put(ExportContractorsCSV.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to export contractors'

    captureSentryError(err, { message: '[saga.contractor.onExportContractorsCSV] error' })

    yield put(ExportContractorsCSV.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export interface FetchContractorsCountsPayload {
  partnerMarketId?: string
  masterContractorRelationId?: string | null
  onlySubcontractors?: boolean
}

function* onFetchContractorsCounts(
  action: AsyncAction<FetchContractorsCountsPayload, ContractorsCounts>,
) {
  const { partnerMarketId, masterContractorRelationId, onlySubcontractors } = action.payload as any

  try {
    const { payload }: { payload: ContractorsCounts } = yield call(
      callSecureApi,
      Endpoints.FetchContractorsCount,
      {
        apiService: ApiService.Paraworks,
        query: {
          partnerMarketId,
          masterContractorRelationId,
          includeOnlySubcontractors: onlySubcontractors,
        },
      },
    )

    yield put(FetchContractorsCounts.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch contractors counts'

    yield put(FetchContractorsCounts.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export interface FetchContractorPayload {
  contractorId: string
  contractorState: ContractorState
}

function* onFetchContractor(action: AsyncAction<FetchContractorPayload, ContractorDetails>) {
  const { contractorId, contractorState } = action.payload

  try {
    const { payload }: { payload: any } = yield call(callSecureApi, Endpoints.FetchContractor, {
      apiService: ApiService.Paraworks,
      method: 'GET',
      query: {
        contractorState,
      },
      params: [
        {
          value: contractorId,
          field: ':contractorId',
        },
      ],
    })

    yield put(FetchContractor.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = 'Failed to get contractor'

    yield put(FetchContractor.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export interface FetchSubcontractorPayload {
  partnerDriverId: string
  contractorState: ContractorState
}

function* onFetchSubcontractor(action: AsyncAction<FetchSubcontractorPayload, ContractorDetails>) {
  const { partnerDriverId, contractorState } = action.payload

  try {
    const { payload }: { payload: any } = yield call(callSecureApi, Endpoints.FetchSubcontractor, {
      apiService: ApiService.Paraworks,
      method: 'GET',
      query: {
        contractorState,
        partnerDriverId,
      },
    })

    yield put(FetchSubcontractor.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = 'Failed to get contractor'

    yield put(FetchSubcontractor.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface SendRemainderContractorPayload {
  partnerDriverId: string
}

function* onSendRemainderContractor(action: AsyncAction<SendRemainderContractorPayload>) {
  const { partnerDriverId } = action.payload

  try {
    const { payload }: { payload: SendReminderResponse } = yield call(
      callSecureApi,
      Endpoints.SendRemainderContractor,
      {
        apiService: ApiService.Paraworks,
        method: 'POST',
        params: [
          {
            value: partnerDriverId,
            field: ':partnerDriverId',
          },
        ],
      },
    )

    yield put(SendRemainderContractor.success())

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to resend invite'

    yield put(SendRemainderContractor.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface ContractorActivitiesLogsPayload {
  partnerDriverId: string
}

function* onFetchContractorActivitiesLogs(
  action: AsyncAction<ContractorActivitiesLogsPayload, Array<ContractorActivitiesLog>>,
) {
  const { partnerDriverId } = action.payload

  try {
    const {
      payload,
    }: {
      payload: Array<ContractorActivitiesLog>
    } = yield call(callSecureApi, Endpoints.FetchContractorActivitiesLogs, {
      apiService: ApiService.Paraworks,
      method: 'GET',
      params: [
        {
          value: partnerDriverId,
          field: ':partnerDriverId',
        },
      ],
    })

    yield put(FetchContractorActivitiesLogs.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch contractor activities logs'
    yield put(FetchContractorActivitiesLogs.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onFetchSubcontractorCounts(action: AsyncAction<void, any>) {
  try {
    const { payload }: { payload: any } = yield call(
      callSecureApi,
      Endpoints.FetchSubcontractorCounts,
      {
        apiService: ApiService.Paraworks,
      },
    )

    yield put(FetchSubcontractorCounts.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch sub-contractors counts'

    yield put(FetchSubcontractorCounts.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UpdateSubcontractorMarketRequest {
  marketId: string
  partnerDriverId: string
}

function* onUpdateSubcontractorMarket(
  action: AsyncAction<UpdateSubcontractorMarketRequest, { id: string }>,
) {
  const { marketId, partnerDriverId } = action.payload

  try {
    const { payload }: { payload: { id: string } } = yield call(
      callSecureApi,
      Endpoints.UpdateSubcontractorMarket,
      {
        method: 'POST',
        apiService: ApiService.Paraworks,
        body: {
          marketId,
          partnerDriverId,
        },
      },
    )

    yield put(UpdateSubcontractorMarket.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to update subcontractor marker'

    yield put(UpdateSubcontractorMarket.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UpdateSubcontractorExternalIdRequest {
  externalId: string
  partnerDriverId: string
}

function* onUpdateSubcontractorExternalId(
  action: AsyncAction<UpdateSubcontractorExternalIdRequest, { id: string }>,
) {
  const { partnerDriverId, externalId } = action.payload

  try {
    const { payload }: { payload: { id: string } } = yield call(
      callSecureApi,
      Endpoints.UpdateSubcontractorExternalId,
      {
        method: 'POST',
        returnResponse: true,
        apiService: ApiService.Paraworks,
        body: {
          partnerDriverId,
          externalId,
        },
      },
    )

    yield put(UpdateSubcontractorExternalId.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to update subcontractor external id'

    yield put(UpdateSubcontractorExternalId.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export interface FetchContractorSelfiePayload {
  contractorId: string
  stepId: string
}

export interface FetchSensitiveDataPayload {
  partnerDriverId: string
  stepId: string
}

function* onFetchContractorSelfie(action: AsyncAction<FetchContractorSelfiePayload, ArrayBuffer>) {
  const { contractorId, stepId } = action.payload

  try {
    const { payload }: { payload: ArrayBuffer } = yield call(
      callSecureApi,
      Endpoints.FetchContractorSelfie,
      {
        apiService: ApiService.Paraworks,
        method: 'GET',
        params: [
          {
            value: contractorId,
            field: ':contractorId',
          },
          {
            value: stepId,
            field: ':stepId',
          },
        ],
        returnBuffer: true,
      },
    )

    yield put(FetchContractorSelfie.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = 'Failed to get contractor selfie'

    yield put(FetchContractorSelfie.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onFetchSensitiveData(action: AsyncAction<FetchSensitiveDataPayload, string>) {
  const { partnerDriverId, stepId } = action.payload

  try {
    const { payload }: { payload: string } = yield call(
      callSecureApi,
      Endpoints.FetchSensitiveData,
      {
        apiService: ApiService.Paraworks,
        method: 'GET',
        params: [
          {
            value: partnerDriverId,
            field: ':partnerDriverId',
          },
          {
            value: stepId,
            field: ':stepId',
          },
        ],
      },
    )

    yield put(FetchSensitiveData.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = 'Failed to fetch sensitive data'

    yield put(FetchSensitiveData.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export interface DeactivationDateOption {
  date: string
  source: string
}

interface FetchContractorDeactivationDatesPayload {
  partnerDriverId: string
}

interface ApiDeactivationDateResponse {
  data: Array<{
    source: string
    date: string
  }>
}

function* onFetchContractorDeactivationDates(
  action: AsyncAction<FetchContractorDeactivationDatesPayload, Array<DeactivationDateOption>>,
) {
  const { partnerDriverId } = action.payload

  try {
    const {
      payload,
    }: {
      payload: ApiDeactivationDateResponse
    } = yield call(callSecureApi, Endpoints.FetchContractorDeactivationDates, {
      apiService: ApiService.Paraworks,
      method: 'GET',
      params: [
        {
          field: ':partnerDriverId',
          value: partnerDriverId,
        },
      ],
    })

    const deactivationDateOptions: DeactivationDateOption[] = payload?.data || []

    yield put(FetchContractorDeactivationDates.success(deactivationDateOptions))

    if (action.next) {
      action.next(null, deactivationDateOptions)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch deactivation dates'

    yield put(FetchContractorDeactivationDates.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface SetContractorDeactivationDatePayload {
  partnerDriverId: string
  deactivationScheduledAt: string
}

function* onSetContractorDeactivationDate(
  action: AsyncAction<SetContractorDeactivationDatePayload, { id: string }>,
) {
  const { partnerDriverId, deactivationScheduledAt } = action.payload

  try {
    const { payload }: { payload: { id: string } } = yield call(
      callSecureApi,
      Endpoints.SetContractorDeactivationDate,
      {
        method: 'POST',
        apiService: ApiService.Paraworks,
        body: {
          deactivationScheduledAt,
        },
        params: [
          {
            field: ':partnerDriverId',
            value: partnerDriverId,
          },
        ],
      },
    )

    yield put(SetContractorDeactivationDate.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to set deactivation date'

    yield put(SetContractorDeactivationDate.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface RemoveContractorDeactivationDatePayload {
  partnerDriverId: string
}

function* onRemoveContractorDeactivationDate(
  action: AsyncAction<RemoveContractorDeactivationDatePayload, { id: string }>,
) {
  const { partnerDriverId } = action.payload

  try {
    const { payload }: { payload: { id: string } } = yield call(
      callSecureApi,
      Endpoints.SetContractorDeactivationDate,
      {
        method: 'POST',
        apiService: ApiService.Paraworks,
        params: [
          {
            field: ':partnerDriverId',
            value: partnerDriverId,
          },
        ],
      },
    )

    yield put(RemoveContractorDeactivationDate.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to remove deactivation date'

    yield put(RemoveContractorDeactivationDate.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* contractorSaga() {
  yield takeLatest(FetchContractors.type.REQUEST, onFetchContractors)
  yield takeEvery(ExportContractorsCSV.type.REQUEST, onExportContractorsCSV)
  yield takeEvery(FetchContractorsCounts.type.REQUEST, onFetchContractorsCounts)
  yield takeLatest(FetchContractor.type.REQUEST, onFetchContractor)
  yield takeEvery(SendRemainderContractor.type.REQUEST, onSendRemainderContractor)
  yield takeEvery(FetchContractorActivitiesLogs.type.REQUEST, onFetchContractorActivitiesLogs)
  yield takeEvery(FetchSubcontractorCounts.type.REQUEST, onFetchSubcontractorCounts)
  yield takeLatest(FetchSubcontractor.type.REQUEST, onFetchSubcontractor)
  yield takeEvery(UpdateSubcontractorMarket.type.REQUEST, onUpdateSubcontractorMarket)
  yield takeEvery(UpdateSubcontractorExternalId.type.REQUEST, onUpdateSubcontractorExternalId)
  yield takeEvery(FetchContractorSelfie.type.REQUEST, onFetchContractorSelfie)
  yield takeEvery(FetchSensitiveData.type.REQUEST, onFetchSensitiveData)
  yield takeEvery(FetchContractorDeactivationDates.type.REQUEST, onFetchContractorDeactivationDates)
  yield takeEvery(SetContractorDeactivationDate.type.REQUEST, onSetContractorDeactivationDate)
  yield takeEvery(RemoveContractorDeactivationDate.type.REQUEST, onRemoveContractorDeactivationDate)
}
